import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const projectDetails = {
  electra: {
    title: 'Electra — AI-Powered Energy Monitoring',
    subtitle: 'Full-stack ML energy intelligence platform — from raw IoT signals to closed-loop operational optimization.',
    headline: 'Rewriting the Intelligence Layer of Energy Infrastructure',
    pillars: [
      {
        title: 'Closed-Loop Optimization',
        description: 'Production-grade ML models analyze facility consumption patterns in real time — transforming raw sensor telemetry into actionable optimization directives that achieve 12–18% waste reduction without human intervention in the loop.'
      },
      {
        title: 'Operator Intelligence',
        description: 'Shifting facility operators from passive consumers to active energy architects — equipping them with predictive dashboards that surface anomalies, forecast demand curves, and prescribe corrective actions before inefficiency compounds.'
      },
      {
        title: 'Edge-First Architecture',
        description: 'Engineering edge-first infrastructure that processes IoT signals locally at sub-120ms latency — building the distributed intelligence layer that makes centralized cloud dependency obsolete at the facility level.'
      }
    ],
    tech: ['Python', 'IoT', 'Power BI', 'Time-series Analytics', 'Edge Devices'],
    overview: 'Electra is a full-stack AI energy intelligence platform built for commercial and industrial environments. It integrates IoT sensor arrays with production ML models to detect consumption anomalies, forecast demand curves, and generate optimization directives — processing 50K+ signals daily at sub-120ms latency and achieving 12–18% operational waste reduction across pilot deployments.',
    impact: [
      '12–18% operational waste reduction across pilot deployments',
      'Forecast accuracy within ±6% on weekly demand curves',
      'Real-time anomaly detection with sub-120ms dashboard response'
    ],
    features: [
      'Real-time consumption monitoring across facility zones',
      'ML-based anomaly detection and alerting',
      'Predictive demand forecasting',
      'Interactive Power BI executive dashboards',
      'Edge computing for low-latency local processing',
      'Automated closed-loop optimization recommendations'
    ]
  },
  tetra: {
    title: 'Assistive Device — Tetra Society',
    subtitle: 'Precision assistive hardware engineered from first principles around user biomechanics and functional independence.',
    headline: 'Engineering Functional Independence at the Individual Level',
    pillars: [
      {
        title: 'Biomechanics-First Design',
        description: 'Every mechanism begins with research at the user level — mapping individual capability profiles and biomechanical constraints before a single line of CAD is drawn, ensuring the output solves the actual problem rather than an assumed one.'
      },
      {
        title: 'Compressed Iteration Cycles',
        description: 'Collapsing the design-test-refine loop with parametric CAD and FDM manufacturing — compressing prototype cycles from weeks to days and enabling high-fidelity ergonomic validation at every iteration before final production commitment.'
      },
      {
        title: 'Quantified Independence',
        description: 'Impact is measured in functional independence gained — a 35% reduction in actuation effort translates directly to extended daily use, reduced fatigue, and meaningful quality-of-life improvement for mobility-impaired individuals.'
      }
    ],
    tech: ['SolidWorks', 'Arduino', 'FDM Prototyping'],
    overview: 'Partnered with the Tetra Society to design and engineer custom assistive devices at the intersection of precision mechanics and human-centred design. Each device was built to individual client specifications through a structured cycle of biomechanical research, parametric CAD modelling, rapid FDM prototyping, and iterative ergonomic validation.',
    impact: [
      '35% reduction in actuation effort across all delivered devices',
      'Full prototype-to-delivery cycle completed in under 3 weeks',
      'User satisfaction rated 4.8 / 5.0 post-deployment'
    ],
    features: [
      'Biomechanics-centred user research',
      'Parametric CAD modelling',
      'Arduino-based control systems',
      'FDM rapid prototyping',
      'Ergonomic validation and iteration',
      'User-led acceptance testing'
    ]
  },
  '3d-design': {
    title: '3D Mechanical & Product Design',
    subtitle: 'Parametric mechanical systems built for manufacturability — precision components, complex assemblies, and high-fidelity visualization.',
    headline: 'Where Engineering Precision Meets Manufacturing Reality',
    pillars: [
      {
        title: 'Manufacturing-First Geometry',
        description: 'Every model is designed with the factory floor embedded in the geometry — tolerances, material behaviour, and production constraints are resolved at the CAD stage, cutting downstream DFM issues by 22% before a single physical part is committed to production.'
      },
      {
        title: 'High-Fidelity Visualization',
        description: 'Translating engineering geometry into KeyShot renders that communicate design intent with precision — collapsing the feedback loop between engineering decisions and stakeholder alignment, eliminating misinterpretation cycles.'
      },
      {
        title: 'Parametric Architecture',
        description: 'Fully parametric models in SolidWorks and Fusion 360 that propagate design changes across entire assemblies instantaneously — eliminating the rework overhead that kills velocity in complex multi-body mechanical programmes.'
      }
    ],
    tech: ['SolidWorks', 'Fusion 360', 'KeyShot', 'CAD/CAM'],
    overview: 'A portfolio of precision mechanical engineering work spanning complex multi-body assemblies, tolerance-controlled component design, and high-fidelity product visualization — all built with a manufacturing-first mindset that minimizes downstream rework and accelerates time to production.',
    impact: [
      '22% reduction in downstream DFM issues across delivered components',
      '50+ precision parts designed and production-ready',
      '98% client satisfaction across all design engagements'
    ],
    features: [
      'Parametric solid modelling',
      'Multi-body assembly design and simulation',
      'Photorealistic product rendering',
      'GD&T specification and technical documentation',
      'Manufacturing drawing packages',
      'Design for manufacturing (DFM) analysis'
    ]
  }
};

export default function ProjectModal({ isOpen, onClose, projectId }) {
  const project = projectDetails[projectId];

  // iOS-safe scroll lock + ESC handler. On iOS Safari `body { overflow: hidden }`
  // alone does not reliably lock — use the classic `position: fixed` + top=-scrollY
  // pattern so the modal's own overflow-y-auto can capture touch scroll.
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);

    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!project) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop — heavier blur so modal clearly pops forward */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 bg-black/75 backdrop-blur-md z-[60]"
            onClick={onClose}
            data-testid="modal-backdrop"
          />

          {/* Scrollable wrapper — handles iOS touch scroll AND click-outside */}
          <div
            className="fixed inset-0 z-[61] overflow-y-auto"
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
            data-testid="modal-scroll-wrapper"
          >
            <div
              className="min-h-full flex items-center justify-center p-3 sm:p-8"
              onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 28 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 16 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-5xl"
                data-testid={`project-modal-${projectId}`}
              >
              {/* Outer Hyper-Cyan accent frame — subtle glow ring */}
              <div
                className="absolute -inset-[1px] pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,240,255,0.55) 0%, rgba(0,240,255,0.1) 30%, rgba(59,130,246,0.2) 70%, rgba(0,240,255,0.55) 100%)',
                  filter: 'blur(0.5px)',
                }}
                aria-hidden="true"
              />

              {/* Card body */}
              <div className="relative bg-[#0B0E13] border border-white/10 shadow-[0_30px_80px_rgba(0,240,255,0.18),0_0_0_1px_rgba(0,240,255,0.12)]">

                {/* Corner brackets — HUD theme */}
                <span className="absolute -top-[1px] -left-[1px] w-4 h-4 border-t-2 border-l-2 border-[var(--hyper-cyan)] pointer-events-none" aria-hidden="true" />
                <span className="absolute -top-[1px] -right-[1px] w-4 h-4 border-t-2 border-r-2 border-[var(--hyper-cyan)] pointer-events-none" aria-hidden="true" />
                <span className="absolute -bottom-[1px] -left-[1px] w-4 h-4 border-b-2 border-l-2 border-[var(--hyper-cyan)] pointer-events-none" aria-hidden="true" />
                <span className="absolute -bottom-[1px] -right-[1px] w-4 h-4 border-b-2 border-r-2 border-[var(--hyper-cyan)] pointer-events-none" aria-hidden="true" />

                {/* Top diagnostic bar — reinforces the "instrument panel" feel */}
                <div className="flex items-center justify-between px-5 sm:px-6 py-2.5 border-b border-white/10 bg-black/35">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--hyper-cyan)] animate-pulse" style={{ boxShadow: '0 0 6px var(--hyper-cyan)' }} />
                    <span className="font-mono text-[9px] sm:text-[10px] tracking-[0.32em] uppercase text-[var(--hyper-cyan)]">
                      DEEP-DIVE / DIAGNOSTIC
                    </span>
                    <span className="hidden sm:inline font-mono text-[9px] tracking-[0.22em] uppercase text-white/28 ml-2">
                      // {projectId}
                    </span>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-7 h-7 flex items-center justify-center border border-white/15 hover:border-[var(--hyper-cyan)] hover:bg-[var(--hyper-cyan)]/10 transition-all"
                    data-testid="modal-close-btn"
                    aria-label="Close"
                  >
                    <X size={14} className="text-white/70" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-5 sm:p-7 md:p-9 space-y-7">
                  {/* Header */}
                  <div>
                    <h1 className="font-headline text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter uppercase leading-[0.98] mb-2.5">
                      {project.title}
                    </h1>
                    <p className="text-white/55 font-rajdhani text-base sm:text-lg leading-snug max-w-3xl">
                      {project.subtitle}
                    </p>
                  </div>

                  {/* Tech row */}
                  <div className="flex flex-wrap gap-1.5">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-0.5 font-mono text-[9px] tracking-[0.22em] uppercase bg-white/5 border border-white/10 text-white/70"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Overview + Impact side-by-side */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
                    <div className="md:col-span-3">
                      <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">
                        Overview
                      </h2>
                      <p className="text-white/70 font-rajdhani text-base leading-relaxed">
                        {project.overview}
                      </p>
                    </div>
                    <div className="md:col-span-2 bg-white/[0.03] border border-white/10 p-5 relative">
                      <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-[var(--hyper-cyan)] pointer-events-none" />
                      <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-[var(--hyper-cyan)] pointer-events-none" />
                      <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--hyper-cyan)] mb-3">
                        Impact
                      </h2>
                      <ul className="space-y-2.5">
                        {project.impact.map((item, index) => (
                          <li key={index} className="flex gap-2.5">
                            <span className="w-1.5 h-1.5 mt-[7px] rounded-full bg-[var(--hyper-cyan)] shrink-0" style={{ boxShadow: '0 0 6px var(--hyper-cyan)' }} />
                            <span className="text-white/80 font-rajdhani text-sm leading-snug">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Headline + Pillars */}
                  <div>
                    <h2 className="font-headline text-xl sm:text-2xl font-black tracking-tight uppercase gradient-text mb-4 leading-tight">
                      {project.headline}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {project.pillars.map((pillar, index) => (
                        <motion.div
                          key={pillar.title}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.08 * index }}
                          className="bg-white/[0.03] border border-white/10 p-4 hover:border-[var(--hyper-cyan)]/45 hover:bg-[var(--hyper-cyan)]/[0.03] transition-all"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--hyper-cyan)]" style={{ boxShadow: '0 0 5px var(--hyper-cyan)' }} />
                            <h3 className="font-mono text-[10px] tracking-[0.22em] uppercase text-white">
                              {pillar.title}
                            </h3>
                          </div>
                          <p className="text-white/55 font-rajdhani text-sm leading-snug">
                            {pillar.description}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Key Features — last block, NO bottom margin */}
                  <div>
                    <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--hyper-cyan)] mb-3">
                      Key Features
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                      {project.features.map((feature, index) => (
                        <div key={index} className="flex gap-2.5">
                          <span className="text-[var(--hyper-cyan)] text-xs mt-1">▸</span>
                          <span className="text-white/70 font-rajdhani text-[15px] leading-snug">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bottom status bar — closes the "instrument panel" frame */}
                <div className="flex items-center justify-between px-5 sm:px-6 py-2 border-t border-white/10 bg-black/35 font-mono text-[9px] tracking-[0.28em] uppercase text-white/35">
                  <span><span className="text-[var(--hyper-cyan)]">●</span> status: verified</span>
                  <span className="hidden sm:inline">deployment: production</span>
                  <span>esc to close</span>
                </div>
              </div>
            </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  // Portal modal to document.body so ancestor CSS transforms (from framer-motion
  // page transitions) do not break `position: fixed` positioning.
  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
